/**
 * EmailRecipientsInput Component
 *
 * Dynamic list of email inputs for sending reports to multiple recipients.
 * Supports 1-10 email addresses (for couples with separate emails).
 */

import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import FormHelperText from "@mui/material/FormHelperText";

const MAX_RECIPIENTS = 10;

export interface EmailRecipientsInputProps {
  value: string[];
  onChange: (emails: string[]) => void;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export function EmailRecipientsInput({
  value,
  onChange,
  error,
  helperText,
  disabled = false,
}: EmailRecipientsInputProps) {
  // Ensure at least one email input
  const emails = value.length > 0 ? value : [""];

  const handleEmailChange = (index: number, newEmail: string) => {
    const updated = [...emails];
    updated[index] = newEmail;
    onChange(updated);
  };

  const handleAddRecipient = () => {
    if (emails.length < MAX_RECIPIENTS) {
      onChange([...emails, ""]);
    }
  };

  const handleRemoveRecipient = (index: number) => {
    if (emails.length > 1) {
      const updated = emails.filter((_, i) => i !== index);
      onChange(updated);
    }
  };

  const canAddMore = emails.length < MAX_RECIPIENTS;
  const canRemove = emails.length > 1;

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <Typography variant="subtitle2">Email Recipients</Typography>
        <Chip
          label={`${emails.filter((e) => e.trim()).length}/${MAX_RECIPIENTS}`}
          size="small"
          color={emails.length >= MAX_RECIPIENTS ? "warning" : "default"}
        />
      </Stack>

      <Stack spacing={1}>
        {emails.map((email, index) => (
          <Stack
            key={index}
            direction="row"
            spacing={1}
            alignItems="flex-start"
          >
            <TextField
              value={email}
              onChange={(e) => handleEmailChange(index, e.target.value)}
              placeholder={index === 0 ? "Primary email" : `Email ${index + 1}`}
              type="email"
              size="small"
              fullWidth
              disabled={disabled}
              error={email.trim() !== "" && !isValidEmail(email)}
              helperText={
                email.trim() !== "" && !isValidEmail(email)
                  ? "Invalid email format"
                  : undefined
              }
              InputProps={{
                startAdornment:
                  index === 0 ? (
                    <Chip
                      label="Primary"
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ mr: 1, height: 20, fontSize: "0.7rem" }}
                    />
                  ) : null,
              }}
            />
            {canRemove && (
              <IconButton
                onClick={() => handleRemoveRecipient(index)}
                disabled={disabled}
                size="small"
                color="error"
                aria-label={`Remove email ${index + 1}`}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        ))}
      </Stack>

      {canAddMore && (
        <Button
          startIcon={<AddIcon />}
          onClick={handleAddRecipient}
          size="small"
          disabled={disabled}
          sx={{ mt: 1 }}
        >
          Add Recipient
        </Button>
      )}

      {error && helperText && (
        <FormHelperText error sx={{ mt: 1 }}>
          {helperText}
        </FormHelperText>
      )}

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 1 }}
      >
        Each recipient will receive a separate copy of the report email.
      </Typography>
    </Box>
  );
}
